import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { staggerContainerVariants, fadeUpVariants } from './useScrollReveal';

const services = [
  {
    id: 'design',
    num: '01',
    title: 'Web Design & Development',
    desc: 'We design stunning, conversion-focused websites that look great on every screen and every device. Built to perform, built to last.',
    tags: ['Web Design', 'UI/UX', 'Responsive', 'SEO'],
    visual: (
      <div className="w-full h-full flex items-center justify-center p-6">
        <div className="w-full max-w-xs">
          <div className="h-2 bg-[#C8F135]/20 rounded mb-3 w-3/4" />
          <div className="h-2 bg-[#1e1e1e] rounded mb-2 w-full" />
          <div className="h-2 bg-[#1e1e1e] rounded mb-5 w-5/6" />
          <div className="grid grid-cols-3 gap-2">
            {[1,2,3].map(i => <div key={i} className="h-16 rounded-lg bg-[#141414] border border-[#1e1e1e]" />)}
          </div>
          <div className="mt-4 h-8 rounded-full bg-[#C8F135]/10 border border-[#C8F135]/20" />
        </div>
      </div>
    ),
  },
  {
    id: 'brand',
    num: '02',
    title: 'Brand Identity',
    desc: 'Logos, color palettes, typography, and brand guidelines that make your business instantly recognizable and unforgettable.',
    tags: ['Logo', 'Style Guide', 'Strategy', 'Identity'],
    visual: (
      <div className="w-full h-full flex items-center justify-center gap-4">
        {['#C8F135','#0f0f0f','#1a1a1a','#333','#555'].map((c, i) => (
          <div
            key={i}
            className="rounded-full border border-[#2a2a2a]"
            style={{
              background: c,
              width: `${52 - i * 6}px`,
              height: `${52 - i * 6}px`,
              boxShadow: i === 0 ? '0 0 20px rgba(200,241,53,0.2)' : 'none',
            }}
          />
        ))}
      </div>
    ),
  },
  {
    id: 'media',
    num: '03',
    title: 'Photo & Video Production',
    desc: 'Professional photography and video production that tells your story and stops the scroll on social media.',
    tags: ['Photography', 'Promo Video', 'Reels', 'Editing'],
    visual: (
      <div className="w-full h-full flex items-center justify-center">
        <div className="relative w-36 h-24 rounded-xl bg-[#111] border border-[#1e1e1e] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 opacity-20" style={{ background: 'linear-gradient(135deg, #C8F135 0%, transparent 60%)' }} />
          <div className="w-0 h-0 z-10" style={{ borderLeft: '20px solid #C8F135', borderTop: '12px solid transparent', borderBottom: '12px solid transparent' }} />
          <div className="absolute bottom-2 right-2 flex gap-1">
            {[4,6,3,7,5].map((h, i) => (
              <div key={i} className="w-1 rounded-t" style={{ height: `${h * 2}px`, background: i === 2 ? '#C8F135' : '#2a2a2a' }} />
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'marketing',
    num: '04',
    title: 'Growth & Marketing',
    desc: 'SEO, paid ads, and social media strategy that puts your business in front of the right people at the right time.',
    tags: ['SEO', 'Google Ads', 'Social Media', 'Analytics'],
    visual: (
      <div className="w-full h-full flex items-end justify-center gap-2 pb-5 px-6">
        {[30, 55, 42, 70, 58, 85, 65, 90].map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t transition-all duration-300"
            style={{
              height: `${h}%`,
              background: i === 7 ? '#C8F135' : i === 5 ? '#C8F13580' : '#1a1a1a',
              boxShadow: i === 7 ? '0 0 12px rgba(200,241,53,0.3)' : 'none',
            }}
          />
        ))}
      </div>
    ),
  },
];

const Services: React.FC = () => {
  const [activeId, setActiveId] = useState<string | null>(null);

  return (
    <section id="services" className="py-32 bg-[#080808] border-t border-[#0f0f0f]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-label mb-5"
          >
            Our Services
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.7, ease: [0.22,1,0.36,1] }}
            className="font-serif text-[clamp(2.2rem,5vw,4.5rem)] text-white leading-tight"
          >
            Expertise That Drives{' '}
            <span className="italic text-lime">Quality</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25 }}
            className="font-dm text-[#999] text-sm mt-5 max-w-sm mx-auto leading-relaxed"
          >
            With deep expertise, we deliver quality solutions that drive success and exceed expectations consistently.
          </motion.p>
        </div>

        {/* Service grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainerVariants}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {services.map((s) => (
            <motion.div
              key={s.id}
              variants={fadeUpVariants}
              className="group border border-[#141414] rounded-2xl overflow-hidden relative cursor-default"
              onMouseEnter={() => setActiveId(s.id)}
              onMouseLeave={() => setActiveId(null)}
              whileHover={{ borderColor: '#C8F13525', y: -4 }}
              transition={{ duration: 0.3 }}
            >
              {/* Visual area */}
              <div className="w-full h-52 bg-[#0a0a0a] relative overflow-hidden border-b border-[#141414]">
                <motion.div
                  className="absolute inset-0"
                  animate={{ opacity: activeId === s.id ? 1 : 0 }}
                  transition={{ duration: 0.4 }}
                  style={{ background: 'radial-gradient(circle at 50% 50%, rgba(200,241,53,0.04), transparent 70%)' }}
                />
                {s.visual}
              </div>

              {/* Content */}
              <div className="p-7">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-syne font-bold text-white text-xl group-hover:text-lime transition-colors duration-300">
                    {s.title}
                  </h3>
                  <span className="font-syne text-xs text-[#222] font-bold mt-1">{s.num}</span>
                </div>
                <p className="font-dm text-[#999] text-sm leading-relaxed mb-5">{s.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {s.tags.map(tag => (
                    <span
                      key={tag}
                      className="font-dm text-xs text-[#888] border border-[#2a2a2a] rounded-full px-3 py-1 group-hover:border-lime/20 group-hover:text-[#bbb] transition-all duration-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Scrolling tag strip */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-10 overflow-hidden border border-[#111] rounded-2xl py-4"
        >
          <div className="flex gap-10 animate-marquee-reverse whitespace-nowrap w-max">
            {['AI-Driven Solutions','Mobile-First','SEO Optimized','Fast Turnaround','Local & National','100% Satisfaction','Results-Driven','Award-Worthy Design',
              'AI-Driven Solutions','Mobile-First','SEO Optimized','Fast Turnaround','Local & National','100% Satisfaction','Results-Driven','Award-Worthy Design'
            ].map((t, i) => (
              <span key={i} className="font-syne text-xs font-bold text-[#1e1e1e] uppercase tracking-widest flex items-center gap-3">
                <span className="text-lime">✦</span> {t}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
